import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  ArrowLeft, CheckCircle, AlertTriangle, Package,
  ChevronDown, ChevronRight, Calendar, Layers,
  Camera, X, ZoomIn, MapPin
} from 'lucide-react';
import { format, parseISO, isValid, isPast } from 'date-fns';

const safeDate = (d) => {
  try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; }
  catch { return '-'; }
};
const projectStatusConfig = {
  active:    { label: 'Active',    color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700'       },
  on_hold:   { label: 'On Hold',   color: 'bg-amber-100 text-amber-700'     },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700'         },
};
const STATUS_LABELS = {
  completed:'Done', in_progress:'In Progress', pending:'Pending',
  waiting:'Waiting', on_hold:'On Hold', not_assigned:'Not Set'
};
const STATUS_COLORS_DOT = {
  completed:'bg-green-500', in_progress:'bg-amber-500',
  pending:'bg-blue-400', waiting:'bg-gray-300',
  on_hold:'bg-orange-400', not_assigned:'bg-gray-200'
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ images, startIdx, onClose }) {
  const [cur, setCur] = useState(startIdx || 0);
  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCur(c => Math.min(c+1, images.length-1));
      if (e.key === 'ArrowLeft')  setCur(c => Math.max(c-1, 0));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [images.length, onClose]);
  const img = images[cur];
  if (!img) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
        <X className="w-5 h-5" />
      </button>
      <p className="absolute top-5 left-1/2 -translate-x-1/2 text-white/40 text-xs">{cur+1} / {images.length}</p>
      <div className="max-w-3xl w-full flex items-center gap-4" onClick={e => e.stopPropagation()}>
        <button onClick={() => setCur(c => Math.max(c-1,0))} disabled={cur===0} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 text-white text-xl flex-shrink-0 flex items-center justify-center">‹</button>
        <img src={img.image_url} alt={img.caption||'Photo'} className="flex-1 max-h-[78vh] object-contain rounded-2xl" />
        <button onClick={() => setCur(c => Math.min(c+1,images.length-1))} disabled={cur===images.length-1} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 text-white text-xl flex-shrink-0 flex items-center justify-center">›</button>
      </div>
      {img.caption && <p className="mt-3 text-white/50 text-sm text-center">{img.caption}</p>}
    </div>
  );
}

// ── Item Stage Tracker ────────────────────────────────────────────────────────
function ItemStageTracker({ item, chains, tasks, imageMap }) {
  const [open, setOpen]           = useState(true);
  const [activeStageKey, setActiveStageKey] = useState(null); // key of expanded stage images
  const [lightbox, setLightbox]   = useState(null);

  const itemChains = chains
    .filter(c => c.project_item_id === item.id)
    .sort((a, b) => a.stage_order - b.stage_order);

  const itemTasks = tasks
    .filter(t => t.project_item_id === item.id)
    .sort((a, b) => (a.stage_order||0) - (b.stage_order||0));

  const stages = itemChains.length > 0
    ? itemChains.map(chain => {
        const task = itemTasks.find(t =>
          Number(t.stage_order) === Number(chain.stage_order) &&
          Number(t.department_id) === Number(chain.department_id)
        );
        return { chain, task };
      })
    : itemTasks.map((task, i) => ({
        chain: { stage_order: task.stage_order||i+1, dept_name: task.department_name, dept_color: task.dept_color, department_id: task.department_id },
        task
      }));

  const completedStages = stages.filter(s => s.task?.status === 'completed').length;
  const totalStages = stages.length;
  const overallPct = totalStages > 0 ? Math.round((completedStages/totalStages)*100) : 0;

  // Find current active stage (first non-completed)
  const currentIdx = stages.findIndex(s => s.task?.status === 'in_progress') ??
    stages.findIndex(s => s.task && s.task.status !== 'completed');

  const handleStageClick = (key, images) => {
    if (images.length === 0) return; // no images — don't toggle
    setActiveStageKey(prev => prev === key ? null : key);
  };

  return (
    <>
      {lightbox && <Lightbox images={lightbox.images} startIdx={lightbox.idx} onClose={() => setLightbox(null)} />}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Item header */}
        <button onClick={() => setOpen(o => !o)}
          className="w-full text-left flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors">
          {item.thumbnail_url ? (
            <div className="w-12 h-12 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0">
              <img src={item.thumbnail_url} alt={item.item_name} className="w-full h-full object-cover"
                onError={e => { e.target.parentElement.className='w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0'; e.target.replaceWith(Object.assign(document.createElement('span'),{innerHTML:'📦',className:'text-xl'})); }} />
            </div>
          ) : (
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="w-5 h-5 text-amber-700" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">{item.item_name}</span>
              {item.proto_code && (
                <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{item.proto_code}</span>
              )}
              <span className="text-xs text-gray-400">Qty: {item.quantity} {item.unit}</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-[160px]">
                <div className={`h-1.5 rounded-full transition-all ${overallPct===100?'bg-emerald-500':overallPct>0?'bg-amber-500':'bg-gray-300'}`}
                  style={{ width: `${overallPct}%` }} />
              </div>
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{completedStages}/{totalStages} stages</span>
            </div>
          </div>
          <div className="flex-shrink-0 text-gray-400">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </button>

        {/* Stage chain — horizontal like admin */}
        {open && (
          <div className="border-t border-gray-100 px-4 pb-4 pt-4">
            {stages.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No stages set for this item yet</p>
            ) : (
              <>
                {/* Horizontal stage flow */}
                <div className="overflow-x-auto pb-2">
                  <div className="flex items-center gap-1 min-w-max">
                    {stages.map(({ chain, task }, idx) => {
                      const status = task?.status || 'not_assigned';
                      const pct    = task && task.quantity_assigned > 0
                        ? Math.round((task.quantity_completed/task.quantity_assigned)*100) : 0;
                      const isCurrentStage = idx === currentIdx;
                      const isCompleted    = status === 'completed';
                      const isActive       = status === 'in_progress';
                      const imageKey = `${item.id}_${chain.stage_order}_${chain.department_id}`;
                      const stageImages = imageMap[imageKey] || [];
                      const hasImages = stageImages.length > 0;
                      const isExpanded = activeStageKey === imageKey;

                      return (
                        <React.Fragment key={idx}>
                          <button
                            onClick={() => handleStageClick(imageKey, stageImages)}
                            className={`flex flex-col items-center w-[84px] flex-shrink-0 transition-all focus:outline-none ${hasImages ? 'cursor-pointer' : 'cursor-default'}`}
                            title={hasImages ? `Click to ${isExpanded?'hide':'view'} ${stageImages.length} photo${stageImages.length>1?'s':''}` : chain.dept_name}
                          >
                            <div className={`w-[76px] rounded-xl p-2 text-center border-2 transition-all ${
                              isCurrentStage && !isCompleted
                                ? 'border-slate-800 bg-slate-800 shadow-lg scale-105'
                                : isCompleted   ? 'border-green-400 bg-green-50 hover:shadow-md'
                                : isActive      ? 'border-amber-400 bg-amber-50 hover:shadow-md'
                                : status==='pending' ? 'border-blue-200 bg-blue-50'
                                : 'border-gray-200 bg-gray-50'
                            } ${hasImages ? 'ring-2 ring-offset-1 ring-indigo-200' : ''} ${isExpanded ? 'ring-indigo-500' : ''}`}>
                              <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${
                                isCurrentStage && !isCompleted ? 'bg-white animate-pulse' : (STATUS_COLORS_DOT[status]||'bg-gray-300')
                              }`} />
                              <p className={`text-[11px] font-semibold leading-tight break-words hyphens-auto ${isCurrentStage&&!isCompleted?'text-white':'text-gray-700'}`}>
                                {chain.dept_name}
                              </p>
                              {task && (
                                <p className={`text-[10px] font-bold mt-0.5 ${isCurrentStage&&!isCompleted?'text-white/80':'text-gray-500'}`}>
                                  {pct}%
                                </p>
                              )}
                              <span className={`text-[9px] font-semibold mt-0.5 inline-block ${
                                isCurrentStage&&!isCompleted?'text-white/70'
                                :isCompleted?'text-green-700'
                                :isActive?'text-amber-700'
                                :status==='pending'?'text-blue-600':'text-gray-400'
                              }`}>{STATUS_LABELS[status]}</span>
                              {hasImages && (
                                <div className={`mt-1 flex items-center justify-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded-md ${
                                  isCurrentStage&&!isCompleted?'bg-white/20 text-white':'bg-indigo-100 text-indigo-600'
                                }`}>
                                  <Camera className="w-2.5 h-2.5" />{stageImages.length}
                                </div>
                              )}
                            </div>
                            <p className={`text-[10px] mt-1 ${isCurrentStage&&!isCompleted?'text-slate-600 font-bold':'text-gray-400'}`}>
                              #{chain.stage_order}
                            </p>
                          </button>

                          {idx < stages.length-1 && (
                            <div className={`text-base flex-shrink-0 pb-4 ${isCompleted?'text-green-400':'text-gray-200'}`}>→</div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {/* Expanded images panel — shown below chain when stage clicked */}
                {activeStageKey && (() => {
                  const expandedStage = stages.find(({ chain }) => {
                    const key = `${item.id}_${chain.stage_order}_${chain.department_id}`;
                    return key === activeStageKey;
                  });
                  const imgs = imageMap[activeStageKey] || [];
                  if (!expandedStage || imgs.length === 0) return null;
                  return (
                    <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-xl p-3 animate-in">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                          <Camera className="w-3.5 h-3.5" />
                          Stage {expandedStage.chain.stage_order}: {expandedStage.chain.dept_name} — {imgs.length} photo{imgs.length>1?'s':''}
                        </p>
                        <button onClick={() => setActiveStageKey(null)} className="text-indigo-400 hover:text-indigo-700">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {imgs.map((img, i) => (
                          <button key={img.id||i} onClick={() => setLightbox({ images: imgs, idx: i })}
                            className="relative w-16 h-16 rounded-xl overflow-hidden border-2 border-white shadow-md hover:shadow-lg group flex-shrink-0 hover:scale-105 transition-all">
                            <img src={img.image_url} alt={img.caption||'Photo'} className="w-full h-full object-cover"
                              onError={e => { e.target.parentElement.style.display='none'; }} />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                              <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ClientProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get(`/client/projects/${id}`)
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.message || 'Error loading project'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );
  if (error) return (
    <div className="text-center py-16">
      <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
      <p className="text-gray-600 font-medium">{error}</p>
      <button onClick={() => navigate('/client/projects')} className="mt-4 text-sm text-amber-600 hover:underline">← Go Back</button>
    </div>
  );

  const { project, items, tasks, chains, imageMap } = data;
  const totalTasks     = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const overallPct     = totalTasks > 0 ? Math.round((completedTasks/totalTasks)*100) : 0;
  const isDelayed      = project.status === 'active' && project.deadline && isPast(parseISO(project.deadline));
  const pst            = projectStatusConfig[project.status] || projectStatusConfig.active;

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      <button onClick={() => navigate('/client/projects')}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Projects
      </button>

      {/* Project Header */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h1 className="text-xl font-bold leading-tight">{project.name}</h1>
              {project.project_id && (
                <span className="text-xs font-mono bg-white/20 px-2 py-0.5 rounded">{project.project_id}</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${pst.color}`}>{pst.label}</span>
              {isDelayed && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-red-400/30 text-red-200 border border-red-400/40 font-medium">⚠ Overdue</span>
              )}
            </div>
          </div>
          <div className="text-center flex-shrink-0">
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" stroke="rgba(255,255,255,0.15)" strokeWidth="6" fill="none" />
                <circle cx="32" cy="32" r="28" stroke={overallPct===100?'#34d399':'#fbbf24'} strokeWidth="6" fill="none"
                  strokeDasharray={`${2*Math.PI*28}`} strokeDashoffset={`${2*Math.PI*28*(1-overallPct/100)}`}
                  strokeLinecap="round" className="transition-all duration-700" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold">{overallPct}%</span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-1">Progress</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-4 border-t border-white/15">
          <div><p className="text-xs text-slate-400 mb-0.5">Order Date</p><p className="text-sm font-semibold">{safeDate(project.order_date)}</p></div>
          <div><p className="text-xs text-slate-400 mb-0.5">Deadline</p><p className={`text-sm font-semibold ${isDelayed?'text-red-300':''}`}>{safeDate(project.deadline)}</p></div>
          <div><p className="text-xs text-slate-400 mb-0.5">Total Items</p><p className="text-sm font-semibold">{items.length}</p></div>
          <div><p className="text-xs text-slate-400 mb-0.5">Stages Done</p><p className="text-sm font-semibold">{completedTasks}/{totalTasks}</p></div>
        </div>
      </div>

      {/* Items header */}
      <div className="flex items-center gap-2">
        <Layers className="w-5 h-5 text-amber-600" />
        <h2 className="font-bold text-gray-900">Items &amp; Stage Tracker</h2>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{items.length} item{items.length!==1?'s':''}</span>
        <span className="text-xs text-gray-400 ml-1">— click a stage to view photos</span>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400">No items in this project yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(item => (
            <ItemStageTracker key={item.id} item={item} chains={chains} tasks={tasks} imageMap={imageMap||{}} />
          ))}
        </div>
      )}

      {project.description && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
          <p className="text-xs font-semibold text-amber-700 mb-1">Project Notes</p>
          <p className="text-sm text-gray-700 leading-relaxed">{project.description}</p>
        </div>
      )}
    </div>
  );
}
